import * as _ from 'lodash'
import { Observable } from 'rxjs'
import { TemplateRef } from '@angular/core'

import { TRoutePage } from 'src/app/infrastructure/services/route-service/models/route-page'
import { ITabDataRequest, TabDataRequest, tabDataRequest } from './tab-data-request'
import { TTabKind, tabKind } from './tab-kind'
import { TListView } from './list-view'

export const tabDataTemplate = {
    ...tabDataRequest,
    kind: 'kind',
    onClick_btnAdd: 'onClick_btnAdd',
    btnAdd_show: 'btnAdd_show',
    btnAdd_tooltip: 'btnAdd_tooltip',
    imageClaimPage: 'imageClaimPage',
    listView: 'listView',
    ownerClaim: 'ownerClaim',
    save$: 'save$',
    templateFooter: 'templateFooter',
    templateButtons: 'templateButtons',
    templateList: 'templateList',
} as const

export type TTabDataTemplate = (typeof tabDataTemplate)[keyof typeof tabDataTemplate];

// Gemeenschappelijke kern die elke tab (lijst óf paneel) nodig heeft.
type TTabDataSourceBase =
    Pick<
        ITabDataTemplate,
        typeof tabDataTemplate.name |
        typeof tabDataTemplate.label |
        typeof tabDataTemplate.templateList
    > & Partial<ITabDataTemplate>

// Lijst-tab: toont een lijst entiteiten; vereist zoekvelden + request. 'kind' is
// optioneel (default 'list') zodat bestaande lijst-tabs ongewijzigd blijven.
export type TTabDataSourceList = TTabDataSourceBase
    & Pick<ITabDataTemplate, typeof tabDataTemplate.searchFields | typeof tabDataTemplate.requestName>
    & { [tabDataTemplate.kind]?: typeof tabKind.list }

// Paneel-tab: toont statische inhoud i.p.v. een lijst; alleen de kern is nodig,
// 'kind: panel' is verplicht om het onderscheid expliciet te maken.
export type TTabDataSourcePanel = TTabDataSourceBase
    & { [tabDataTemplate.kind]: typeof tabKind.panel }

export type TTabDataSource = TTabDataSourceList | TTabDataSourcePanel

// Eigenaarschap-bypass voor tab-zichtbaarheid (RecordOwner/EntityOwner, zie
// UserClaimController/ApplicationUserService.ownerClaim_check): __sourceList_checkAuth toont de tab
// ook als de reguliere claim ontbreekt, mits de kijker de eigenaar is (ownerGuid-match) én de
// gereserveerde functie een Page_Read-claim heeft op deze tab.
export interface ITabOwnerClaim {
    kind: 'record' | 'entity'
    ownerGuid: string | null
}

export interface ITabDataTemplate
    extends ITabDataRequest
{
    [tabDataTemplate.kind]: TTabKind
    [tabDataTemplate.onClick_btnAdd]: () => void | null
    [tabDataTemplate.btnAdd_show]: () => boolean | null
    [tabDataTemplate.btnAdd_tooltip]: string | null
    [tabDataTemplate.imageClaimPage]: TRoutePage | null
    // Weergave-versie van de lijst in deze tab (zie list-view.ts); null = de enige/standaardversie.
    [tabDataTemplate.listView]: TListView | null
    [tabDataTemplate.ownerClaim]: ITabOwnerClaim | null
    [tabDataTemplate.save$]: ((dto: any) => Observable<any>) | null
    [tabDataTemplate.templateFooter]: TemplateRef<any> | undefined | null
    [tabDataTemplate.templateButtons]: TemplateRef<any> | undefined | null
    [tabDataTemplate.templateList]: TemplateRef<any> | undefined | null
}

export class TabDataTemplate
    extends TabDataRequest
    implements ITabDataTemplate
{
    [tabDataTemplate.kind]: TTabKind = tabKind.list;
    [tabDataTemplate.onClick_btnAdd]: () => void | null = () => null;
    [tabDataTemplate.btnAdd_show]: () => boolean | null = () => null;
    [tabDataTemplate.btnAdd_tooltip]: string | null = null;
    [tabDataTemplate.imageClaimPage]: TRoutePage | null = null;
    [tabDataTemplate.listView]: TListView | null = null;
    [tabDataTemplate.ownerClaim]: ITabOwnerClaim | null = null;
    [tabDataTemplate.save$]: ((dto: any) => Observable<any>) | null = null;
    [tabDataTemplate.templateFooter]: TemplateRef<any> | undefined | null = null;
    [tabDataTemplate.templateButtons]: TemplateRef<any> | undefined | null = null;
    [tabDataTemplate.templateList]: TemplateRef<any> | undefined | null = null;

    constructor()
    constructor(data: TTabDataSource | null)
    constructor(data?: Partial<ITabDataTemplate> | null) {
        super((data ?? null) as ITabDataRequest)
        if (!data) return

        Object.assign(this, TabDataTemplate.merge(this, data))
    }

    static merge(current: ITabDataTemplate, data: Partial<ITabDataTemplate>): ITabDataTemplate {
        const next = {...current}
        next[tabDataTemplate.kind] = data[tabDataTemplate.kind] ?? tabKind.list;
        next[tabDataTemplate.onClick_btnAdd] = data[tabDataTemplate.onClick_btnAdd] ?? (() => null);
        next[tabDataTemplate.btnAdd_show] = data[tabDataTemplate.btnAdd_show] ?? (() => null);
        next[tabDataTemplate.btnAdd_tooltip] = data[tabDataTemplate.btnAdd_tooltip] ?? null;
        next[tabDataTemplate.imageClaimPage] = data[tabDataTemplate.imageClaimPage] ?? null;
        next[tabDataTemplate.listView] = data[tabDataTemplate.listView] ?? null;
        next[tabDataTemplate.ownerClaim] = data[tabDataTemplate.ownerClaim] ?? null;
        next[tabDataTemplate.save$] = data[tabDataTemplate.save$] ?? null;
        next[tabDataTemplate.templateFooter] = data[tabDataTemplate.templateFooter] ?? null;
        next[tabDataTemplate.templateButtons] = data[tabDataTemplate.templateButtons] ?? null;
        next[tabDataTemplate.templateList] = data[tabDataTemplate.templateList] ?? null;

        return next
    }
}
